import {
  boldFont,
  getRelativeFontSize,
  mediumFont,
  regularFont,
  theme,
} from "../../utils/styles";

const aboutUsStyles = {
  // ── Wrapper (matches PrivacyPolicy) ──────────────────────────────────────
  content: {
    width: "100%",
    minHeight: "calc(100vh - 165px)",
    height: "auto",
    marginTop: "120px",
    [theme.breakpoints.down("sm")]: {
      marginTop: "100px",
      minHeight: "calc(100vh - 100px)",
    },
  },
  mainContainer: {
    display: "flex",
    justifyContent: "center",
    mx: 5,
    [theme.breakpoints.down("sm")]: {
      mx: 2,
    },
  },
  mainBox: {
    p: 1,
    borderRadius: "20px",
    minHeight: "calc(100vh - 220px)",
    background: "rgba(255, 255, 255, 0.07)",
    backdropFilter: "blur(30px)",
    border: "1px solid rgba(255, 255, 255, 0.14)",
    boxShadow: "0 4px 30px rgba(0, 0, 0, 0.1)",
    margin: "25px 0px",
    maxWidth: "1750px",
    width: "100%",
  },
  subBox: {
    p: 3,
    minHeight: "calc(100vh - 260px)",
    borderRadius: "20px",
    border: "1px solid rgba(255, 255, 255, 0.14)",
  },

  // ── Hero ─────────────────────────────────────────────────────────────────
  heroWrapper: {
    position: "relative",
    textAlign: "center" as const,
    pt: { xs: 3, md: 5 },
    pb: { xs: 3, md: 5 },
    overflow: "hidden",
  },
  heroGlowTop: {
    position: "absolute",
    top: "-120px",
    left: "50%",
    transform: "translateX(-50%)",
    width: { xs: "400px", md: "700px" },
    height: { xs: "400px", md: "700px" },
    borderRadius: "50%",
    background:
      "radial-gradient(circle, rgba(144,83,246,0.18) 0%, rgba(75,1,80,0.1) 40%, transparent 70%)",
    pointerEvents: "none",
  },
  heroGlowBottom: {
    position: "absolute",
    bottom: "-80px",
    right: "10%",
    width: "300px",
    height: "300px",
    borderRadius: "50%",
    background:
      "radial-gradient(circle, rgba(75,1,80,0.15) 0%, transparent 70%)",
    pointerEvents: "none",
  },
  heroBadge: {
    display: "inline-flex",
    alignItems: "center",
    gap: 1,
    px: 2.5,
    py: 0.75,
    borderRadius: "50px",
    background: "rgba(144, 83, 246, 0.12)",
    border: "1px solid rgba(144, 83, 246, 0.32)",
    mb: 4,
  },
  heroBadgeDot: {
    width: "7px",
    height: "7px",
    borderRadius: "50%",
    background: "rgba(176,122,246,1)",
    boxShadow: "0 0 6px rgba(176,122,246,0.8)",
    flexShrink: 0,
  },
  heroBadgeText: {
    ...mediumFont,
    fontSize: getRelativeFontSize(1),
    color: "rgba(176, 122, 246, 0.95)",
    letterSpacing: "2.5px",
    textTransform: "uppercase" as const,
  },
  heroTitle: {
    ...boldFont,
    fontSize: { xs: "52px", sm: "72px", md: "96px" },
    background:
      "linear-gradient(120deg, #FFFFFF 0%, #c49cf8 35%, #9053F6 60%, #FFFFFF 100%)",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    backgroundClip: "text",
    letterSpacing: { xs: "4px", md: "8px" },
    lineHeight: 1.25,
    mb: 4,
    textShadow: "none",
  },
  heroAccentBar: {
    width: "80px",
    height: "3px",
    background:
      "linear-gradient(90deg, transparent, #9053F6, #c49cf8, transparent)",
    mx: "auto",
    mb: 4,
    borderRadius: "2px",
  },
  heroSubtitle: {
    ...regularFont,
    fontSize: { xs: getRelativeFontSize(4), md: getRelativeFontSize(6) },
    color: "rgba(255,255,255,0.62)",
    maxWidth: "680px",
    mx: "auto",
    lineHeight: 1.8,
    mb: 5,
  },
  // Three hero feature chips
  heroChipsRow: {
    display: "flex",
    flexWrap: "wrap" as const,
    justifyContent: "center",
    gap: 2,
  },
  heroChip: {
    display: "inline-flex",
    alignItems: "center",
    gap: 1,
    px: 2.5,
    py: 1,
    borderRadius: "10px",
    background: "rgba(255,255,255,0.04)",
    border: "1px solid rgba(255,255,255,0.1)",
    transition: "border-color 0.2s ease",
    "&:hover": { borderColor: "rgba(144,83,246,0.45)" },
  },
  heroChipEmoji: {
    fontSize: "18px",
    lineHeight: 1,
  },
  heroChipText: {
    ...mediumFont,
    fontSize: getRelativeFontSize(2),
    color: "rgba(255,255,255,0.72)",
    letterSpacing: "0.3px",
  },

  // ── Gradient section divider ───────────────────────────────────────────────
  gradientDivider: {
    height: "1px",
    background:
      "linear-gradient(90deg, transparent, rgba(144,83,246,0.4), rgba(255,255,255,0.1), rgba(144,83,246,0.4), transparent)",
    border: "none",
    my: 6,
  },

  // ── Section header ────────────────────────────────────────────────────────
  sectionLabelBox: {
    display: "inline-flex",
    alignItems: "center",
    gap: 1,
    px: 2,
    py: 0.5,
    borderRadius: "6px",
    background: "rgba(144, 83, 246, 0.1)",
    border: "1px solid rgba(144, 83, 246, 0.25)",
    mb: 2,
  },
  sectionLabel: {
    ...mediumFont,
    fontSize: getRelativeFontSize(1),
    color: "rgba(176,122,246,0.9)",
    letterSpacing: "1.5px",
    textTransform: "uppercase" as const,
  },
  sectionTitle: {
    ...boldFont,
    fontSize: { xs: getRelativeFontSize(11), md: getRelativeFontSize(16) },
    color: "#FFFFFF",
    lineHeight: 1.15,
    mb: 1,
  },
  sectionBar: {
    width: "48px",
    height: "2px",
    background: "linear-gradient(90deg, #9053F6, rgba(144,83,246,0.3))",
    borderRadius: "1px",
    my: 2.5,
  },

  // ── Project / About content ───────────────────────────────────────────────
  // Lead paragraph — large opening statement
  leadParagraph: {
    ...mediumFont,
    fontSize: { xs: getRelativeFontSize(5), md: getRelativeFontSize(7) },
    color: "rgba(255,255,255,0.88)",
    lineHeight: 1.85,
    mb: 4,
  },
  // Mission callout
  missionBox: {
    pl: { xs: 3, md: 4 },
    pr: { xs: 3, md: 4 },
    pt: { xs: 2.5, md: 3 },
    pb: { xs: 2.5, md: 3 },
    mb: 4,
    borderRadius: "0 14px 14px 0",
    background: "rgba(144,83,246,0.07)",
    borderLeft: "3px solid #9053F6",
  },
  missionLabel: {
    ...mediumFont,
    fontSize: getRelativeFontSize(1),
    color: "rgba(176,122,246,0.75)",
    letterSpacing: "2px",
    textTransform: "uppercase" as const,
    mb: 1.5,
  },
  missionText: {
    ...mediumFont,
    fontSize: { xs: getRelativeFontSize(4), md: getRelativeFontSize(5) },
    color: "rgba(255,255,255,0.9)",
    lineHeight: 1.85,
  },
  bodyParagraph: {
    ...regularFont,
    fontSize: getRelativeFontSize(3),
    color: "rgba(255,255,255,0.65)",
    lineHeight: 1.95,
    mb: 2.5,
  },
  bulletsIntro: {
    ...mediumFont,
    fontSize: getRelativeFontSize(3),
    color: "rgba(255,255,255,0.78)",
    mb: 2,
    mt: 1,
    lineHeight: 1.7,
  },
  bulletRow: {
    display: "flex",
    alignItems: "flex-start",
    gap: 2,
    mb: 1.5,
    pl: 0.5,
  },
  bulletIconBox: {
    width: "22px",
    height: "22px",
    borderRadius: "6px",
    background: "rgba(144,83,246,0.2)",
    border: "1px solid rgba(144,83,246,0.35)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    mt: "3px",
  },
  bulletIcon: {
    width: "8px",
    height: "8px",
    borderRadius: "50%",
    background: "rgba(176,122,246,0.9)",
  },
  bulletText: {
    ...regularFont,
    fontSize: getRelativeFontSize(3),
    color: "rgba(255,255,255,0.72)",
    lineHeight: 1.75,
  },
  // Pioneering closing statement
  pioneerBox: {
    mt: 4,
    p: { xs: 3, md: 4.5 },
    borderRadius: "18px",
    background:
      "linear-gradient(135deg, rgba(144,83,246,0.1) 0%, rgba(75,1,80,0.08) 100%)",
    borderTop: "2px solid rgba(144,83,246,0.5)",
    border: "1px solid rgba(144,83,246,0.2)",
    borderTopWidth: "2px",
    borderTopColor: "rgba(144,83,246,0.5)",
    textAlign: "center" as const,
  },
  pioneerStar: {
    ...boldFont,
    fontSize: "20px",
    color: "rgba(144,83,246,0.7)",
    mb: 2,
    letterSpacing: "6px",
  },
  pioneerText: {
    ...mediumFont,
    fontSize: { xs: getRelativeFontSize(4), md: getRelativeFontSize(5) },
    color: "rgba(255,255,255,0.85)",
    lineHeight: 1.9,
  },

  // ── Capabilities ──────────────────────────────────────────────────────────
  // Fix for overlap: Grid items must have display:flex — set on Grid item via sx prop in TSX
  capCard: {
    p: { xs: 2.5, md: 3.5 },
    borderRadius: "18px",
    background: "rgba(255,255,255,0.03)",
    borderTop: "2px solid rgba(144,83,246,0.5)",
    border: "1px solid rgba(255,255,255,0.07)",
    borderTopWidth: "2px",
    borderTopColor: "rgba(144,83,246,0.5)",
    flex: 1,
    display: "flex",
    flexDirection: "column" as const,
    gap: 2,
    transition:
      "transform 0.28s ease, box-shadow 0.28s ease, background 0.28s ease",
    "&:hover": {
      transform: "translateY(-6px)",
      background: "rgba(144,83,246,0.07)",
      boxShadow:
        "0 16px 40px rgba(144,83,246,0.18), 0 0 0 1px rgba(144,83,246,0.25)",
    },
  },
  capCardHeader: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
  },
  capIconBox: {
    width: "56px",
    height: "56px",
    borderRadius: "16px",
    background:
      "linear-gradient(135deg, rgba(144,83,246,0.35) 0%, rgba(75,1,80,0.35) 100%)",
    border: "1px solid rgba(144,83,246,0.25)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "26px",
    boxShadow: "0 4px 16px rgba(144,83,246,0.2)",
  },
  capNumber: {
    ...boldFont,
    fontSize: getRelativeFontSize(14),
    color: "rgba(144,83,246,0.12)",
    lineHeight: 1,
    letterSpacing: "-1px",
  },
  capTitle: {
    ...boldFont,
    fontSize: getRelativeFontSize(6),
    color: "#FFFFFF",
    lineHeight: 1.3,
  },
  capDesc: {
    ...regularFont,
    fontSize: getRelativeFontSize(2),
    color: "rgba(255,255,255,0.55)",
    lineHeight: 1.8,
    flexGrow: 1,
  },

  // ── Legal ─────────────────────────────────────────────────────────────────
  legalCard: {
    p: { xs: 3, md: 5 },
    borderRadius: "20px",
    background: "rgba(255,255,255,0.03)",
    border: "1px solid rgba(255,255,255,0.1)",
    overflow: "hidden",
    position: "relative",
  },
  legalCardGlow: {
    position: "absolute",
    top: "-100px",
    right: "-100px",
    width: "350px",
    height: "350px",
    borderRadius: "50%",
    background:
      "radial-gradient(circle, rgba(144,83,246,0.07) 0%, transparent 70%)",
    pointerEvents: "none",
  },
  legalCompanyName: {
    ...boldFont,
    fontSize: { xs: getRelativeFontSize(10), md: getRelativeFontSize(14) },
    color: "#FFFFFF",
    letterSpacing: { xs: "2px", md: "4px" },
    textAlign: "center" as const,
    mb: 1,
  },
  legalCompanyTagline: {
    ...regularFont,
    fontSize: getRelativeFontSize(2),
    color: "rgba(255,255,255,0.35)",
    letterSpacing: "1px",
    textAlign: "center" as const,
    textTransform: "uppercase" as const,
    mb: 4,
  },
  legalDivider: {
    height: "1px",
    background: "rgba(255,255,255,0.07)",
    border: "none",
    mb: 3,
  },
  legalRow: {
    display: "flex",
    flexDirection: { xs: "column", sm: "row" } as const,
    gap: { xs: 0.5, sm: 4 },
    py: 2.5,
    borderBottom: "1px solid rgba(255,255,255,0.05)",
    "&:last-child": {
      borderBottom: "none",
      pb: 0,
    },
  },
  legalLabel: {
    ...mediumFont,
    fontSize: getRelativeFontSize(2),
    color: "rgba(176,122,246,0.8)",
    minWidth: { sm: "220px" },
    flexShrink: 0,
    letterSpacing: "0.3px",
  },
  legalValue: {
    ...regularFont,
    fontSize: getRelativeFontSize(3),
    color: "rgba(255,255,255,0.82)",
    lineHeight: 1.65,
  },
} as const;

export default aboutUsStyles;
